<template>
    <ui-modal>
        <template #header>
            <h3 class="mb-0" v-text="'Autoryzacja usługi'" />
        </template>

        <form v-if="!isLoading">
            <div class="a-infoBar a-infoBar--warning text-sm mb-4">
                Wprowadź hasło do Panelu EuroPlatform, aby rozpocząć korzystanie
                z usługi {{ drsService.name }}. <br />
                <strong v-text="'UWAGA!'" /> Jeśli nie pamiętasz hasła, wyloguj się,
                skorzystaj z opcji resetu hasła i zaloguj się ponownie. Po zalogowaniu włącz usługę jeszcze raz
            </div>

            <the-input
                width="200px"
                readonly
                class="drs-cred-input mb-4"
                label="Adres e-mail/login"
                :text="email"
            />
            <the-input
                type="password"
                class="drs-cred-input mb-0"
                label="Wpisz swoje hasło"
                :invalid="!!passwordError"
                :invalid-feedback="passwordError"
                :invalid-icon="false"
                v-model="password"
            />
        </form>
        <div v-else class="text-center">
            <page-preloader
                :min-height="(formHeight || 60) + 'px'"
                loading-status="loading"
                :text="!isInitialLoading ? 'Trwa uruchamianie usługi...' : ''"
            />
        </div>

        <template #footer>
            <the-btn
                class="btn-outline-custom"
                text="Wyloguj się"
                @click="logout"
            />
            <the-btn
                :disabled="isLoading"
                class="btn-secondary"
                text="Potwierdź"
                @click="startProcess"
            />
        </template>
    </ui-modal>
</template>

<script>
import { mapActions } from 'vuex'

export default {
    data: () => ({
        formHeight: 0,
        password: '',
        passwordError: '',
        isInitialLoading: false,
        isLoading: false,
        isDestroyed: false
    }),

    async created() {
        this.isLoading = true
        this.isInitialLoading = true
        await this.$store.dispatch('servicesFetch')
        this.isInitialLoading = false
        this.isLoading = false
    },

    computed: {
        email() {
            return this.$store.getters.getUser.email
        },

        passwordLocal: {
            get() {
                return this.password
            },
            set(value) {
                this.$emit('update:password', value)
            }
        },

        serviceCode() {
            return this.$store.getters.modalState.args.serviceCode
        },

        drsService() {
            return this.$store.getters.getServiceByCode(this.serviceCode)
        },

        userId() {
            return this.$store.getters.getUser.id
        },

        shopIds() {
            return this.$store.getters.modalState.args.shopIds ||
                this.$store.getters.getToActiveServiceShopIds
        },

        hasLocationsToMap() {
            return this.$store.getters.modalState.args.hasLocationsToMap
        }
    },

    methods: {
        ...mapActions('drs', ['loadDataAndCheckPermission']),

        async startProcess() {
            if (this.isLoading) {
                return
            }
            this.isLoading = true
            this.passwordError = ''

            try {
                await this.checkCredentials()

                await this.activateService()

                let progress = await this.checkProgress()

                const waitForCompletion = async () => {
                    if (this.isDestroyed) return
                    const locations = progress

                    const allDone = locations.every(l => l.state !== null)

                    if (!allDone) {
                        // If not done yet, wait 1.5s and check again
                        await new Promise(resolve => setTimeout(resolve, 1500))

                        // Check progress
                        progress = await this.checkProgress()

                        // Recursively do the function again
                        await waitForCompletion()
                    }
                    if (!this.isDestroyed) {
                        await this.loginDrs()
                        let mappedSubscriptionsSummary = {}

                        // Update subscriptions for mapped locations
                        if (this.hasLocationsToMap) {
                            mappedSubscriptionsSummary = await this.$store.dispatch(
                                'prepareMappedSubscriptionsSummary'
                            )
                        }

                        await this.loadDataAndCheckPermission()

                        this.$store.dispatch('toggleModal', false)

                        this.$nextTick(() => {
                            this.$store.dispatch('openModal', {
                                componentPath: 'drs/service/Summary',
                                args: {
                                    serviceCode: this.serviceCode,
                                    isLocationMappingProcess: this.hasLocationsToMap,
                                    ...mappedSubscriptionsSummary
                                }
                            })
                        })
                    }
                }
                await waitForCompletion()
            } catch (error) {
                console.error('Activation failed:', error)
            } finally {
                this.isLoading = false
            }
        },

        async checkCredentials() {
            try {
                await this.$store.$api.post('credentials_check', {
                    login: this.$store.getters.getUser.email,
                    password: this.password
                })
            } catch (error) {
                if (error.response?.status === 400) {
                    this.passwordError = 'Błędne hasło'
                } else {
                    this.passwordError = 'Błąd podczas weryfikacji hasła'
                }
                throw error
            }
        },

        /**
         * Search for any active DRS service in the shops
         * If found, throw error, and do not allow to activate the service again
         */
        async isDrsAlreadyActivated() {
            try {
                const { data, status } = await this.$store.$api.get(
                    `subscriptions_services/${this.userId}/services/${this.drsService.id}`,
                    {
                        params: {
                            subscriptionsWithEntityData: true
                        }
                    }
                )

                if (status !== 200 || !data?.data) {
                    throw new Error('Failed to fetch DRS shops data')
                }

                const subscriptions = data.data.subscriptions || []

                const drsShops = subscriptions.filter(sub => this.shopIds.includes(sub.entityData.id))

                return drsShops.some(shop => shop.status === 'active')
            } catch (error) {
                console.error('Failed to fetch DRS shops:', error)
            }
        },

        async activateService() {
            try {
                if (await this.isDrsAlreadyActivated() === true) {
                    return
                }

                // If the service is not active in any shop, proceed with activation
                await this.$store.$api.patch(
                    `subscriptions_services_on_off/users/${this.userId}/services/${this.drsService.id}/activate`, {
                        activation_level: this.drsService.level.toUpperCase(),
                        objectIds: this.shopIds
                    }
                )

                // Map locations
                if (this.hasLocationsToMap) {
                    await this.$store.dispatch('mapDrsLocations')
                }

                await this.$store.dispatch('replicateDrsLocations', { shopIds: this.shopIds })
            } catch (error) {
                console.error('Failed to activate DRS service:', error)
            }
        },

        async checkProgress() {
            try {
                const { data, status } = await this.$store.$api.get(`users/${this.userId}/replication/location/status`, {
                    params: {
                        locationIds: this.shopIds.join(',')
                    }
                })

                if (status !== 200 || !data?.data) {
                    throw new Error('Failed to fetch DRS service progress')
                }

                return data.data.locations || []
            } catch (error) {
                console.error('Failed to check DRS service progress:', error)
            }
        },

        setFormHeight() {
            // Set the form height after a short delay to ensure the DOM is updated
            setTimeout(() => {
                this.formHeight = this.$el.querySelector('form').offsetHeight
            }, 500)
        },

        async loginDrs() {
            return this.$store.$api.post('drs/login', {
                login: this.$store.getters.getUser.email,
                password: this.password
            })
        },

        logout() {
            this.$emit('close-modal')
            this.$router.push({ name: 'logout' })
        }
    },

    mounted() {
        // Check form height after the component is mounted
        this.setFormHeight()
    },

    beforeDestroy() {
        this.isDestroyed = true
    },

    watch: {
        isLoading(newValue) {
            if (newValue) {
                this.passwordLocal = ''
            }
        }
    }
}
</script>

<style lang="scss">
.drs-cred-input {
    margin-left: auto;
    margin-right: auto;
    width: 500px;
    max-width: 100%;;
}
</style>